import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useAnnouncementFormSetup,
    VALIDATION_LIMITS_ANNOUNCEMENT,
} from "@/hse/features/Dashboard/Announcements/AnnouncementForm/formHooks";
import type { Announcement } from "@dashboard/api/task";

interface AddEditAnnouncementModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    announcement?: Announcement;
}

export function AddEditAnnouncementModal({
    open,
    onOpenChange,
    announcement,
}: AddEditAnnouncementModalProps) {
    const { t } = useTranslation();
    const { useAnnouncementForm, formOpts, mapAnnouncementToForm, handleAnnouncementSubmit } =
        useAnnouncementFormSetup();
    const [submitError, setSubmitError] = useState<string | null>(null);

    const isEdit = !!announcement;

    const form = useAnnouncementForm({
        defaultValues: announcement ? mapAnnouncementToForm(announcement) : formOpts.defaultValues,
        validators: formOpts.validators,
        onSubmit: async ({ value }) => {
            setSubmitError(null);

            const result = await handleAnnouncementSubmit(value, announcement?.id);
            if (!result.success) {
                setSubmitError(t("common.errorSavingAnnouncement"));
                return;
            }
            onOpenChange(false);
        },
    });

    const handleOpenChange = (v: boolean) => {
        if (form.state.isSubmitting) return;
        if (!v) setSubmitError(null);
        onOpenChange(v);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto gap-0 p-0">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void form.handleSubmit();
                    }}
                >
                    <DialogHeader className="p-5 border-b border-compendia-border">
                        <DialogTitle className="text-lg font-bold text-compendia-foreground">
                            {isEdit ? t("announcement.edit") : t("announcement.addNew")}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex flex-col gap-5 p-5">
                        <form.AppField
                            name="icon"
                            children={(field) => (
                                <field.IconSelect label={t("announcement.iconLabel")} />
                            )}
                        />

                        <form.AppField
                            name="title"
                            children={(field) => (
                                <field.Input
                                    label={t("announcement.titleLabel")}
                                    placeholder={t("announcement.titlePlaceholder")}
                                    maxLength={VALIDATION_LIMITS_ANNOUNCEMENT.TITLE_MAX}
                                    showCharCount={true}
                                />
                            )}
                        />

                        <form.AppField
                            name="content"
                            children={(field) => (
                                <field.TextArea
                                    label={t("announcement.contentLabel")}
                                    placeholder={t("announcement.contentPlaceholder")}
                                    maxLength={VALIDATION_LIMITS_ANNOUNCEMENT.CONTENT_MAX}
                                    rows={5}
                                    showCharCount={true}
                                />
                            )}
                        />

                        {submitError && (
                            <p role="alert" className="text-chameleon-pink text-sm">
                                {submitError}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="flex-row justify-end border-t border-compendia-border p-5">
                        <form.Subscribe selector={(state) => [state.isSubmitting, state.canSubmit]}>
                            {([isSubmitting, canSubmit]) => (
                                <>
                                    <Button
                                        variant="outline"
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => handleOpenChange(false)}
                                    >
                                        {t("common.cancel")}
                                    </Button>

                                    <Button
                                        variant="submit"
                                        size="submit"
                                        type="submit"
                                        disabled={!canSubmit}
                                    >
                                        {isEdit ? t("common.save") : t("announcement.publish")}
                                    </Button>
                                </>
                            )}
                        </form.Subscribe>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
